import block from 'bem-cn-lite';
import {compose} from 'recompose';
import React, {useState, useCallback, useEffect, useMemo, Children} from 'react';
import {Col, Grid, GridColumnSize, Row} from '@yandex-data-ui/page-constructor';

import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import withServicesData, {WithServicesDataProps} from 'hoc/withServicesData';
import withSolutionsData, {WithSolutionsDataProps} from 'hoc/withSolutionsData';

import Search from 'units/marketplace/components/Search/Search';
import NavigationMenu from 'units/navigation/components/NavigationMenu/NavigationMenu';
import NavigationItems from 'units/navigation/components/NavigationItems/NavigationItems';
import {FEATURED} from 'units/services/constants';
import {NavigationSectionType} from 'units/navigation/models';
import {NavigationData} from 'models/app';

import './ExtendedPopup.scss';

const b = block('extended-popup');

interface ExtendedPopupProps {
    type?: string;
}

export type ExtendedPopupExtendProps = ExtendedPopupProps &
    WithServicesDataProps &
    WithSolutionsDataProps &
    WithTranslationProps;

const ExtendedPopup: React.FC<ExtendedPopupExtendProps> = (props) => {
    const {
        i18n,
        servicesData,
        solutionsData,
        children,
        type = NavigationSectionType.Services,
    } = props;

    const popupData = useMemo((): NavigationData => {
        switch (type) {
            case NavigationSectionType.Solutions:
                return solutionsData;
            case NavigationSectionType.Services:
            default:
                return servicesData;
        }
    }, [type, solutionsData, servicesData]);

    const {categories, dataByCategory, products} = popupData;

    const [search, setSearch] = useState('');
    const [category, setCategory] = useState(categories[0]);

    const onSearch = useCallback((value: string) => setSearch(value), []);
    const onCategoryChange = useCallback(
        (value: string) => {
            const newCategory = categories.find(({slug}) => slug === value);

            if (newCategory) {
                setCategory(newCategory);
            }
        },
        [categories],
    );

    const menuItems = useMemo(
        () =>
            categories.map(({slug, name}) => ({
                title: name,
                id: slug,
                isFeatured: slug === FEATURED,
            })),
        [categories],
    );

    useEffect(() => setSearch(''), [category]);

    const renderNavigation = () => (
        <Col
            className={b('navigation-wrapper')}
            sizes={{[GridColumnSize.Lg]: 3, [GridColumnSize.All]: 4}}
        >
            <div className={b('navigation')}>
                <NavigationMenu
                    className={b('menu')}
                    items={menuItems}
                    active={category.slug}
                    onChange={onCategoryChange}
                />
                <Search
                    initialValue=""
                    onSubmit={onSearch}
                    className={b('search')}
                    placeholder={i18n(type, 'search-placeholder')}
                    value={search}
                    size="s"
                />
            </div>
        </Col>
    );

    const renderListByCategories = () => {
        return (
            <Col
                className={b('list-by-categories')}
                sizes={{[GridColumnSize.Lg]: 9, [GridColumnSize.All]: 8}}
            >
                <NavigationItems
                    size="s"
                    search={search}
                    dataByCategory={dataByCategory}
                    products={products}
                    categories={[category]}
                    location="popup"
                    type={type as NavigationSectionType}
                />
            </Col>
        );
    };

    const renderChildren = () =>
        Children.map(children, (child, index) => <Col key={index}>{child}</Col>);

    return (
        <Grid className={b()}>
            <Row>
                {renderNavigation()}
                {renderListByCategories()}
            </Row>
            <Row className={b('footer')}>{renderChildren()}</Row>
        </Grid>
    );
};

export default compose<ExtendedPopupExtendProps, ExtendedPopupProps>(
    withTranslation(),
    withServicesData,
    withSolutionsData,
)(ExtendedPopup);
